// useProducts.js — v11
// CHANGED FROM v10 (6 Aug 2026): REAL FIX — convertDriveUrl() now routes
// images through Netlify's own Image CDN (/.netlify/images) instead of
// requesting the resized version directly from Google's servers. Netlify
// fetches each image from Google ONCE, transforms and caches it on its
// own fast servers — every visitor after that is served the fast cached
// copy, and Google is never touched again for that image. Google Drive
// remains the source of truth; nothing about the upload/manual-intake
// workflow changes. Requires the [images] remote_images allowlist added
// to netlify.toml v3. Same 800px width kept, now applied via Netlify's
// own w= parameter instead of Google's.
import { useState, useEffect } from 'react';
const SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTNnc0G3-nxE4w0Hf7pDvUWXmGqADYIuD5wtGovUYd2roEyokgUXlUh3psffIhC9b596_PgS1krm5C0/pub?gid=462276089&single=true&output=csv';
const NO_SIZE_KEYWORDS = ['handbag', 'bag', 'jewel', 'watch', 'sunglass', 'belt', 'sock'];
const IMAGE_WIDTH = 800;
function hasNoSizing(mainProdFolder) {
  const f = (mainProdFolder || '').toLowerCase();
  return NO_SIZE_KEYWORDS.some(kw => new RegExp(`\\b${kw}\\b`).test(f));
}
function parseSizes(sizeStr) {
  if (!sizeStr) return [];
  const trimmed = sizeStr.trim();
  if (trimmed === '' || trimmed.toUpperCase() === 'N/A') return [];
  return trimmed.split('-').map(s => s.trim()).filter(Boolean);
}
function convertDriveUrl(url) {
  if (!url) return '';
  const match = url.match(/id=([a-zA-Z0-9_-]+)/);
  if (match) {
    const googleUrl = `https://lh3.googleusercontent.com/d/${match[1]}`;
    return `/.netlify/images?url=${encodeURIComponent(googleUrl)}&w=${IMAGE_WIDTH}`;
  }
  return url;
}
function getCategory(mainProdFolder) {
  const f = mainProdFolder?.toLowerCase() || '';
  if (f.includes('handbag') || f.includes('bag')) return 'Bags';
  if (f.includes('watch')) return 'Watches';
  if (f.includes('jewel')) return 'Jewellery';
  if (f.includes('footwear') || f.includes('shoe')) return 'Shoes';
  return 'Garments';
}
function extractProductName(description, subFolder) {
  if (!description) return subFolder || 'Product';
  const cleaned = description
    .replace(/stock clearance sale\.?/i, '')
    .replace(/new arrival\.?/i, '')
    .replace(/sale\.?/i, '')
    .trim();
  const sentences = cleaned.split(/[.!]/);
  for (let s of sentences) {
    const trimmed = s.trim();
    if (trimmed.length > 8 &&
        !trimmed.toLowerCase().includes('dm fast') &&
        !trimmed.toLowerCase().includes('free shipping') &&
        !trimmed.toLowerCase().includes('limited stock') &&
        !trimmed.toLowerCase().includes('all india') &&
        !trimmed.toLowerCase().includes('premium quality ladies') &&
        !trimmed.toLowerCase().includes('read item')) {
      return trimmed.length > 55 ? trimmed.substring(0, 55) + '...' : trimmed;
    }
  }
  return subFolder || 'Product';
}
function parseDate(dateStr) {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  const oldParts = trimmed.split('-');
  if (oldParts.length === 3) {
    const months = { Jan:0, Feb:1, Mar:2, Apr:3, May:4, Jun:5, Jul:6, Aug:7, Sep:8, Oct:9, Nov:10, Dec:11 };
    const day = parseInt(oldParts[0]);
    const month = months[oldParts[1]];
    const year = 2000 + parseInt(oldParts[2]);
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      return new Date(year, month, day);
    }
  }
  const nativeParsed = new Date(trimmed);
  if (!isNaN(nativeParsed.getTime())) {
    return nativeParsed;
  }
  return null;
}
function parseCsvFull(text) {
  const rows = [];
  let row = [];
  let current = '';
  let inQuotes = false;
  const t = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  for (let i = 0; i < t.length; i++) {
    const char = t[i];
    if (inQuotes) {
      if (char === '"') {
        if (t[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = false; }
      } else {
        current += char;
      }
    } else {
      if (char === '"') { inQuotes = true; }
      else if (char === ',') { row.push(current); current = ''; }
      else if (char === '\n') { row.push(current); rows.push(row); row = []; current = ''; }
      else { current += char; }
    }
  }
  if (current !== '' || row.length > 0) { row.push(current); rows.push(row); }
  return rows;
}
export function useProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    async function fetchProducts() {
      try {
        const res = await fetch(SHEET_URL);
        if (!res.ok) throw new Error('Failed to fetch');
        const text = await res.text();
        const allRows = parseCsvFull(text);
        const headers = allRows[0].map(h => h.trim());
        const rows = allRows.slice(1).map(values =>
          headers.reduce((obj, h, i) => ({ ...obj, [h]: (values[i] || '').trim() }), {})
        );
        const mapped = rows
          .filter(row =>
            row['Brand']?.toUpperCase().includes('PRIVATE LABEL') &&
            row['Status']?.toUpperCase() !== 'INACTIVE' &&
            row['Status']?.toUpperCase() !== 'SOLD' &&
            row['Status']?.toUpperCase() !== 'HIDDEN' &&
            row['Prod ID']?.trim() !== ''
          )
          .map((row) => {
            const images = [];
            for (let i = 1; i <= 16; i++) {
              const url = row[`Image URL #${i}`];
              if (url && url.trim() !== '') {
                images.push(convertDriveUrl(url.trim()));
              }
            }
            const updateDate = parseDate(row['Update Date']);
            const status = row['Status'] || '';
            const mainProdFolder = row['Main Prod Folder'] || '';
            const sitePrice = parseFloat(row['Site Price']);
            const rawPrice = parseFloat(row['Price']);
            const finalPrice = !isNaN(sitePrice) && sitePrice > 0 ? sitePrice : (!isNaN(rawPrice) ? rawPrice : 0);
            const nameSource = row['Clean Description']?.trim() ? row['Clean Description'] : row['Description'];
            return {
              id: row['Prod ID'],
              name: extractProductName(nameSource, row['Sub Prod Folder']),
              category: getCategory(mainProdFolder),
              subCategory: mainProdFolder,
              gender: row['Gender'] || '',
              price: finalPrice,
              resellerPrice: 0,
              mrp: 0,
              imageUrl: images[0] || '',
              images: images,
              stock: 10,
              sold: status?.toUpperCase() === 'SOLD',
              updateDate: updateDate,
              description: row['Clean Description']?.trim() ? row['Clean Description'] : row['Description'] || '',
              shipping: row['Shipping Charges'] || '',
              sizes: parseSizes(row['Size']),
              hasSizing: !hasNoSizing(mainProdFolder),
            };
          });
        const byId = new Map();
        mapped.forEach(p => {
          const existing = byId.get(p.id);
          if (!existing) {
            byId.set(p.id, p);
          } else {
            const existingTime = existing.updateDate ? existing.updateDate.getTime() : 0;
            const currentTime = p.updateDate ? p.updateDate.getTime() : 0;
            if (currentTime >= existingTime) byId.set(p.id, p);
          }
        });
        const deduped = Array.from(byId.values())
          .sort((a, b) => {
            if (!a.updateDate) return 1;
            if (!b.updateDate) return -1;
            return b.updateDate - a.updateDate;
          });
        setProducts(deduped);
      } catch (err) {
        setError(err.message);
        setProducts(DEMO_PRODUCTS);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);
  return { products, loading, error };
}
export const DEMO_PRODUCTS = [
  { id: '1', name: 'Linen Co-ord Set', category: 'Garments', price: 1099, resellerPrice: 749, mrp: 1499, imageUrl: '', images: [], stock: 14, sold: false, updateDate: new Date(), shipping: 'FREE SHIP', sizes: [], hasSizing: true },
  { id: '2', name: 'Oxidised Necklace', category: 'Jewellery', price: 849, resellerPrice: 579, mrp: 1199, imageUrl: '', images: [], stock: 22, sold: false, updateDate: new Date(), shipping: 'FREE SHIP', sizes: [], hasSizing: false },
  { id: '3', name: 'Printed Kurti', category: 'Garments', price: 649, resellerPrice: 449, mrp: 899, imageUrl: '', images: [], stock: 18, sold: false, updateDate: new Date(), shipping: 'SHIP EXTRA', sizes: [], hasSizing: true },
  { id: '4', name: 'Gold-tone Bangles', category: 'Jewellery', price: 599, resellerPrice: 409, mrp: 799, imageUrl: '', images: [], stock: 0, sold: false, updateDate: new Date(), shipping: 'FREE SHIP', sizes: [], hasSizing: false },
];